import {
  PlanData,
  paywallPlanBreakpoints,
  planTemplateThresholds,
} from '@shared/models/paywall.model';
import { Project } from '@shared/models/project.model';
import { UserOrganizationMetadata } from '@shared/models/user.model';
import { isBeforeDate } from './date.helpers';
import { DateTime } from 'luxon';
import { isNumeric } from './math.helpers';
import { guards } from './typeguards.helpers';

export const parseMaxProjectCount = (
  maxProjectCount?: UserOrganizationMetadata['max_project_count'] | number,
): number | undefined => {
  if (guards.is.number(maxProjectCount) || guards.is.undefined(maxProjectCount))
    return maxProjectCount;
  if (!isNumeric(maxProjectCount)) return undefined;
  return parseInt(maxProjectCount, 10);
};

export const getPaywallPlan = (
  maxProjectCount?: UserOrganizationMetadata['max_project_count'] | number,
): PlanData['plan'] => {
  const parsedCount = parseMaxProjectCount(maxProjectCount);

  if (parsedCount === undefined) return 'enterprise';
  return paywallPlanBreakpoints.getValue(parsedCount);
};

// Only do this once on mount since isActiveProject function is called a lot
const DATE_ONE_YEAR_AGO = DateTime.now().minus({ years: 1 });

/**
 * Check if a project is active (ie projects that should be counted towards the project limit)
 */
export const isActiveProject = (
  project: Pick<Project, 'name' | 'created_at' | 'template' | 'archived'>,
) => {
  const isOverOneYearOld = isBeforeDate(project.created_at, DATE_ONE_YEAR_AGO);
  return !project.template && !(project.archived && isOverOneYearOld);
};

/**
 * Check if the number of projects has exceeded the allowed limit
 * @param projectCount - The number of active organization projects
 * @param maxProjectCount - Allowed project limit for the organization
 * @returns True if number of projects is over the allowed limit
 */
export const isOverMaxProjectCount = (
  projectCount: number,
  maxProjectCount?: UserOrganizationMetadata['max_project_count'] | number,
): boolean => {
  const parsedMaxCount = parseMaxProjectCount(maxProjectCount);
  return parsedMaxCount !== undefined && projectCount >= parsedMaxCount;
};

/**
 * Check if a certain plan has exceeded the allowed number of project templates
 * @param templateCount - The number of active organization project templates
 * @param plan - The paywall plan of the organization
 * @returns True if number of project templates is over the allowed limit
 */
export const isOverMaxProjectTemplatesCount = (
  templateCount: number,
  plan: PlanData['plan'],
): boolean => {
  return templateCount >= planTemplateThresholds[plan];
};
